import { PantryLocation } from '../../utils/pantryLocation';
import { FoodCategory } from '../../types/foodRecognition';

/** One row of the demo dataset, before category/location/expiry resolution. */
export interface DemoRow {
  name: string;
  nameEn: string;
  frenchCat: string;
  frenchLoc: string;
  days: number | null;
  backdatedDays?: number;
}

export interface DemoItemPayload {
  name: string;
  category: FoodCategory;
  confidence: number;
  location: PantryLocation;
  quantity?: number;
  unit?: string;
  expiry_date?: {
    day: number;
    month: number;
    year: number;
    confidence: number;
    explanation: string;
  };
  dateAdded?: string; // ISO date string for backdated items
  source: 'demo';
}

/**
 * Maps French categories from gm-mk.jsx to app FoodCategory
 */
const categoryMap: Record<string, FoodCategory> = {
  'Viande': 'Meat',
  'Poisson': 'Meat',
  'Légumes': 'Vegetables',
  'Fruits': 'Fruits',
  'Laitier': 'Dairy',
  'Boulangerie': 'Bread/Grains',
  'Condiments': 'Condiments',
  'Garde-manger': 'Packaged Foods',
  'Restes': 'Leftovers',
};

/**
 * Maps French location names to app PantryLocation
 */
const locationMap: Record<string, PantryLocation> = {
  'frigo': 'fridge',
  'congélateur': 'freezer',
  'comptoir': 'counter',
  'garde-manger': 'pantry',
};

/**
 * Raw demo items transcribed from gm-mk.jsx MK array (lines 41-73)
 * Structure: { name, frenchCat, frenchLoc, qty, days, note? }
 */
const rawItems: DemoRow[] = [
  // FRIGO
  { name: 'Poitrines de poulet', nameEn: 'Chicken breasts', frenchCat: 'Viande', frenchLoc: 'frigo', days: 2 },
  { name: 'Filets de saumon', nameEn: 'Salmon fillets', frenchCat: 'Poisson', frenchLoc: 'frigo', days: 1 },
  { name: 'Épinards frais', nameEn: 'Fresh spinach', frenchCat: 'Légumes', frenchLoc: 'frigo', days: 1 },
  { name: 'Fraises', nameEn: 'Strawberries', frenchCat: 'Fruits', frenchLoc: 'frigo', days: 2 },
  { name: 'Yogourt grec', nameEn: 'Greek yogurt', frenchCat: 'Laitier', frenchLoc: 'frigo', days: 5 },
  { name: 'Feta', nameEn: 'Feta', frenchCat: 'Laitier', frenchLoc: 'frigo', days: 6 },
  { name: 'Œufs', nameEn: 'Eggs', frenchCat: 'Laitier', frenchLoc: 'frigo', days: 14 },
  { name: 'Carottes', nameEn: 'Carrots', frenchCat: 'Légumes', frenchLoc: 'frigo', days: 16 },
  { name: 'Lait 2 %', nameEn: '2% milk', frenchCat: 'Laitier', frenchLoc: 'frigo', days: -1 },
  { name: 'Restes de curry', nameEn: 'Leftover curry', frenchCat: 'Restes', frenchLoc: 'frigo', days: null, backdatedDays: 8 },
  { name: 'Crème sûre', nameEn: 'Sour cream', frenchCat: 'Laitier', frenchLoc: 'frigo', days: null },
  // CONGÉLATEUR
  { name: 'Petits pois', nameEn: 'Green peas', frenchCat: 'Légumes', frenchLoc: 'congélateur', days: 90 },
  { name: 'Poulet (surgelé)', nameEn: 'Chicken (frozen)', frenchCat: 'Viande', frenchLoc: 'congélateur', days: 120 },
  { name: 'Bleuets', nameEn: 'Blueberries', frenchCat: 'Fruits', frenchLoc: 'congélateur', days: 90 },
  { name: 'Pains pita', nameEn: 'Pita bread', frenchCat: 'Boulangerie', frenchLoc: 'congélateur', days: 60 },
  // COMPTOIR
  { name: 'Pain au levain', nameEn: 'Sourdough bread', frenchCat: 'Boulangerie', frenchLoc: 'comptoir', days: 2 },
  { name: 'Avocats', nameEn: 'Avocados', frenchCat: 'Fruits', frenchLoc: 'comptoir', days: 2 },
  { name: 'Bananes', nameEn: 'Bananas', frenchCat: 'Fruits', frenchLoc: 'comptoir', days: 3 },
  { name: 'Tomates italiennes', nameEn: 'Roma tomatoes', frenchCat: 'Légumes', frenchLoc: 'comptoir', days: 4 },
  { name: 'Oignons jaunes', nameEn: 'Yellow onions', frenchCat: 'Légumes', frenchLoc: 'comptoir', days: 30 },
  { name: 'Ail', nameEn: 'Garlic', frenchCat: 'Légumes', frenchLoc: 'comptoir', days: 40 },
  // GARDE-MANGER
  { name: 'Riz basmati', nameEn: 'Basmati rice', frenchCat: 'Garde-manger', frenchLoc: 'garde-manger', days: null },
  { name: 'Pâtes', nameEn: 'Pasta', frenchCat: 'Garde-manger', frenchLoc: 'garde-manger', days: null },
  { name: 'Tomates en conserve', nameEn: 'Canned tomatoes', frenchCat: 'Garde-manger', frenchLoc: 'garde-manger', days: null },
  { name: 'Miel', nameEn: 'Honey', frenchCat: 'Condiments', frenchLoc: 'garde-manger', days: null },
  { name: 'Sauce soja', nameEn: 'Soy sauce', frenchCat: 'Condiments', frenchLoc: 'garde-manger', days: null },
  { name: 'Huile d\'olive', nameEn: 'Olive oil', frenchCat: 'Condiments', frenchLoc: 'garde-manger', days: null },
];

/**
 * Builds a demo expiry date object from days relative to today
 * Returns undefined if days is null (no expiry)
 * Uses LOCAL date getters to match pantryFreshnessService.daysUntilExpiry
 */
function buildExpiryDate(days: number | null, referenceDate: Date): DemoItemPayload['expiry_date'] | undefined {
  if (days === null) return undefined;

  const expiryDate = new Date(referenceDate);
  expiryDate.setDate(expiryDate.getDate() + days);

  return {
    day: expiryDate.getDate(),
    month: expiryDate.getMonth() + 1,
    year: expiryDate.getFullYear(),
    confidence: 95,
    explanation: `Estimated shelf life (${days} days from today)`,
  };
}

/**
 * Builds the demo dataset with expiry dates computed relative to today.
 *
 * `pickName` exists so the empty-state preview can render English names
 * without changing what the seed persists. Seeded items are written to
 * Firestore and later matched against recipes by `normalizeIngredientName`,
 * which leans on a hand-maintained FR->EN table — so the persisted `name`
 * stays French and only the display path swaps it. See previewItems.ts.
 *
 * @param today Optional reference date (defaults to current date)
 * @param pickName Which name field to emit; defaults to the French `name`
 * @returns Array of demo items ready for addItemsAndSync dispatch
 */
export function buildDemoItems(
  today?: Date,
  pickName: (row: DemoRow) => string = (row) => row.name,
): DemoItemPayload[] {
  const referenceDate = today || new Date();

  return rawItems.map((raw: any) => {
    const appCategory = categoryMap[raw.frenchCat] || 'Packaged Foods';
    const appLocation = locationMap[raw.frenchLoc] || 'pantry';

    // For items with backdatedDays (like curry added 8 days ago),
    // set dateAdded to 8 days ago so the Leftovers>5d rule puts them in review
    let dateAdded = referenceDate.toISOString();
    if (raw.backdatedDays) {
      const backdated = new Date(referenceDate);
      backdated.setDate(backdated.getDate() - raw.backdatedDays);
      dateAdded = backdated.toISOString();
    }

    const item: DemoItemPayload = {
      name: pickName(raw),
      category: appCategory,
      confidence: 95,
      location: appLocation,
      quantity: 1,
      source: 'demo',
      ...(raw.backdatedDays && { dateAdded }),
    };

    // Add expiry date if days is specified
    if (raw.days !== null) {
      item.expiry_date = buildExpiryDate(raw.days, referenceDate);
    }

    return item;
  });
}
